"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAppSelector } from "@/store";
import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store/store";
import { logout } from "@/store/slices/authSlice";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils/utils";
import { useEntitlementGuard } from "@/components/entitlement-guard/EntitlementGuardProvider";
import { useGetPricingBundleQuery } from "@/store/api/pricingApi";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Pencil,
  Zap,
  Crown,
  Ticket,
  CircleHelp,
  Sparkles,
  ArrowRightFromLine,
  X,
} from "lucide-react";

export default function UserProfilePanel({ onLogout, onNavigateToProfile }: { onLogout?: () => void; onNavigateToProfile?: () => void }) {
  const { currentUser } = useAppSelector((state) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();

  // The account's REAL tier, from the credit service - not the display string
  // on the user row, which can drift from what they are actually subscribed
  // to (see the resolver's accountPlanName vs planName distinction).
  const { plan } = useEntitlementGuard();
  const { data: pricing } = useGetPricingBundleQuery();

  /**
   * What the button should say and do - the fnx equivalent of this panel
   * (`user-profile-panel.tsx`) never actually varies this: its button always
   * reads the generic word "Upgrade", on every tier including the top one.
   * That is vague but at least never wrong. Ours used to hardcode a SPECIFIC
   * tier name ("Upgrade to Pro") regardless of the account's real plan, which
   * is worse - it names the wrong destination outright once someone is
   * already on Pro or above. Since the tier's `weight` is real data here
   * (unlike fnx, which has no clean weight-ordered plan list to compute
   * from), the honest fix is to compute the actual next tier rather than
   * fall back to fnx's vaguer "just say Upgrade".
   *
   * `null` means show nothing: the account is already on the highest tier,
   * and a CTA to upgrade past your own top plan has nothing to offer.
   */
  const nextTier = useMemo(() => {
    const tiers = pricing?.plans ?? [];
    if (tiers.length === 0) return undefined; // still loading - render nothing rather than guess

    // No resolved plan (never subscribed) or an unrecognised key: treat it as
    // the free tier's own weight, so "the next tier up" resolves to the same
    // entry-level paid plan a free account would see. NOT a monthlyPrice
    // check - `free` prices at 0, which is not null, so filtering on
    // "monthlyPrice !== null" wrongly counted free itself as a valid
    // "upgrade target" and produced "Upgrade to Free Forever".
    const freeWeight = tiers.find((t) => t.key === "free")?.weight ?? 0;
    const currentWeight = tiers.find((t) => t.key === plan?.planKey)?.weight ?? freeWeight;

    const above = tiers.filter((t) => t.weight > currentWeight);
    return above.length > 0 ? above.reduce((a, b) => (a.weight < b.weight ? a : b)) : null;
  }, [pricing?.plans, plan?.planKey]);

  // See sidebar-user: AuthGuard performs the redirect once status flips.
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      dispatch(logout({}));
    }
  };

  const handleNavigateToProfile = () => {
    if (onNavigateToProfile) {
      onNavigateToProfile();
    }
    router.push("/settings/profile");
  };

  const name = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "";
  const email = currentUser ? currentUser.email : "example@infynd.ai";
  const initials = currentUser ? `${currentUser.firstName?.[0] ?? "H"}${currentUser.lastName?.[0] ?? "C"}` : "HC";
  // "Free Forever" mirrors the User schema's own default (see api/models/
  // User.ts), not a paid tier - this branch only renders in the moment
  // before currentUser is loaded, and showing "PRO" there was the same class
  // of bug as the hardcoded upgrade button above: a specific paid tier name
  // shown to someone who may not be on it.
  const planName = currentUser ? currentUser.planName : "Free Forever";
  const [showAtlasCard, setShowAtlasCard] = useState(true);
  const [activeQuickHelpItem, setActiveQuickHelpItem] = useState<string | null>(null);

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="max-w-none p-0 h-full shadow-none overflow-auto scrollbar-mini rounded-lg sm:rounded-none">
        <CardContent className="flex flex-col h-full min-h-full p-0">
          <div className="px-4 py-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
              Current Plan
            </div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-gray-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-gray-700">
                <Crown className="h-3.5 w-3.5 text-brand/70" />
                {planName}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-brand/20 px-3 h-7 text-xs font-semibold cursor-pointer transition-colors hover:border-brand/40 hover:bg-gray-50 text-gray-700"
                    onClick={() => router.push("/settings/usage")}
                  >
                    <Zap className="mr-1 h-3.5 w-3.5 text-brand/60" /> Credit Usage
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">View your credit usage</TooltipContent>
              </Tooltip>
            </div>
            {/* Hidden while pricing hasn't loaded yet, and again once loaded if
                nextTier resolves to null - an account already on the top tier
                has nothing to upgrade to, and showing the button anyway is
                exactly the "Upgrade to Pro" bug this replaces, just moved to a
                different wrong tier. */}
            {nextTier !== undefined && nextTier !== null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full h-auto rounded-lg border border-brand/20 bg-brand-soft text-brand text-sm font-semibold py-2.5 cursor-pointer transition-colors hover:bg-brand-soft hover:text-brand hover:border-brand/40"
                    onClick={() => router.push("/settings/pricing")}
                  >
                    <Crown className="h-4 w-4" /> Upgrade to {nextTier.name}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">See plans and pricing</TooltipContent>
              </Tooltip>
            )}
          </div>

          <Separator />

          <div className="px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
              Get Quick Help
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="ghost"
                className={cn(
                  "w-full h-auto justify-start px-2 -mx-2 py-2 gap-3 text-sm font-normal text-gray-800 cursor-pointer transition-colors hover:text-gray-950 hover:bg-gray-100",
                  activeQuickHelpItem === "support-center" && "border border-brand/20 bg-gray-50 text-gray-900 font-medium hover:bg-gray-100"
                )}
                onClick={() => {
                  setActiveQuickHelpItem("support-center");
                  router.push("/support-center");
                }}
              >
                <Ticket className="h-4 w-4 text-brand/60" />
                <span>Support Center</span>
              </Button>
              <Button
                variant="ghost"
                className={cn(
                  "w-full h-auto justify-start px-2 -mx-2 py-2 gap-3 text-sm font-normal text-gray-800 cursor-pointer transition-colors hover:text-gray-950 hover:bg-gray-100",
                  activeQuickHelpItem === "help-docs" && "border border-brand/20 bg-gray-50 text-gray-900 font-medium hover:bg-gray-100"
                )}
                onClick={() => {
                  setActiveQuickHelpItem("help-docs");
                  router.push("/help-docs");
                }}
              >
                <CircleHelp className="h-4 w-4 text-brand/60" />
                <span>Help Docs</span>
              </Button>
            </div>
          </div>

          <div className="flex-1" />

          {showAtlasCard && (
            <div className="mx-4 mb-3 rounded-xl border border-brand/15 bg-gray-50 p-3 relative transition-colors hover:border-brand/25">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute right-1.5 top-1.5 h-6 w-6 text-gray-400 cursor-pointer transition-colors hover:bg-gray-200 hover:text-gray-600"
                    onClick={() => setShowAtlasCard(false)}
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">Dismiss</TooltipContent>
              </Tooltip>
              <div className="flex items-center gap-2 font-semibold text-sm mb-1.5 pr-4">
                <Sparkles className="h-4 w-4 text-brand/70" />
                Meet 27x.ai
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-2">
                27x.ai: An AI SDR that qualifies leads, prioritizes accounts, and runs personalized outreach campaigns end-to-end.
              </p>
              <Button
                variant="link"
                className="h-auto p-0 text-xs font-semibold text-gray-900 cursor-pointer hover:text-brand"
                asChild
              >
                <a href="https://agents.27x.ai/signup" target="_blank" rel="noreferrer noopener">
                  Try 27x.ai Now
                </a>
              </Button>
            </div>
          )}

          <Separator />

          <div className="px-4 py-3 flex items-center gap-3">
            <Avatar className="h-10 w-10 bg-brand/80 shrink-0">
              <AvatarFallback className="text-white font-semibold bg-brand/80 text-sm">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1">
                <span className="truncate">{name}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 shrink-0 text-gray-400 cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
                      onClick={handleNavigateToProfile}
                      aria-label="Edit profile"
                    >
                      <Pencil className="h-3.5 w-3.5 text-black" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Edit profile</TooltipContent>
                </Tooltip>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-xs text-gray-500 truncate w-fit max-w-full cursor-default">{email}</div>
                </TooltipTrigger>
                <TooltipContent side="top">{email}</TooltipContent>
              </Tooltip>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 border-gray-300 cursor-pointer shrink-0 transition-colors hover:bg-red-50 hover:text-brand"
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  <ArrowRightFromLine className="h-4 w-4 text-black" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Logout</TooltipContent>
            </Tooltip>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
